import { Text } from '@/ui-kit'
import { Trans, useLingui } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { Mic, MicOff, PhoneCall, PhoneOff, User } from 'lucide-react'
import type { LiveKitParticipant } from '../../hooks/useLiveKit'

interface ParticipantCirclesProps {
  participants: LiveKitParticipant[]
  isMuted: boolean
  toggleMute: () => void
  requiredCount: number
  readyCount: number
  sessionParticipants?: Array<{ participantIndex: number; isReady: boolean }>
  showReadyStatus?: boolean
}

export function ParticipantCircles({
  participants,
  isMuted,
  toggleMute,
  requiredCount,
  readyCount,
  sessionParticipants = [],
  showReadyStatus = true
}: ParticipantCirclesProps) {
  const { t } = useLingui()

  const slotCount = Math.max(requiredCount, participants.length)
  const slots = Array.from({ length: slotCount }, (_, i) => participants.find((p) => p.participantIndex === i) ?? null)

  return (
    <div className="flex items-center justify-center gap-8 py-4">
      {slots.map((p, i) => {
        if (!p) {
          return (
            <div key={`empty-${i}`} className="flex flex-col items-center gap-2">
              <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
                <div className="bg-black-to-white flex size-24 items-center justify-center rounded-full">
                  <div className="border-gray-80 text-gray-80 relative z-1 flex size-20 items-center justify-center rounded-full bg-gray-100">
                    <User className="size-8" />
                  </div>
                </div>
              </div>

              <Text type="body2-regular" className="text-gray-80">
                {t`Participant ${i + 1}`}
              </Text>

              <div className="flex items-center gap-2">
                <span className={clsx('size-2.5 rounded-full', 'bg-gray-90 animate-pulse')} />
                <Text type="body2-regular" className="text-gray-90">
                  <Trans>Waiting to join...</Trans>
                </Text>
              </div>
            </div>
          )
        }

        const isYou = p.isLocal
        const sessionEntry = sessionParticipants.find((sp) => sp.participantIndex === i)
        const participantIsReady = sessionEntry ? sessionEntry.isReady : i < readyCount
        const badgeStatus = participantIsReady ? 'ready' : 'waiting'
        const participantNumber = p.participantIndex + 1

        return (
          <div key={p.identity} className="flex flex-col items-center gap-2">
            <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
              {p.isSpeaking && (
                <>
                  <div
                    className="bg-green absolute inset-0 rounded-full"
                    style={{ animation: 'nectar-pulse-ring 2s ease-out infinite' }}
                  />
                  <div
                    className="bg-green absolute inset-0 rounded-full opacity-0"
                    style={{ animation: 'nectar-pulse-ring 2s ease-out infinite 0.8s' }}
                  />
                </>
              )}
              <div className="bg-black-to-white flex size-24 items-center justify-center rounded-full">
                {isYou ? (
                  <button
                    type="button"
                    onClick={toggleMute}
                    className={clsx(
                      'relative z-1 flex size-20 items-center justify-center rounded-full transition-all duration-200',
                      p.isSpeaking
                        ? 'bg-green text-white'
                        : isMuted
                          ? 'bg-red text-white'
                          : 'text-gray-80 bg-gray-100 hover:bg-gray-100/80'
                    )}
                  >
                    {isMuted ? <MicOff className="size-8" /> : <Mic className="size-8" />}
                  </button>
                ) : (
                  <div
                    className={clsx(
                      'relative z-1 flex size-20 items-center justify-center rounded-full transition-all duration-200',
                      p.isSpeaking ? 'bg-green text-white' : 'text-gray-80 bg-gray-100'
                    )}
                  >
                    {p.isMuted ? <PhoneOff className="size-8" /> : <PhoneCall className="size-8" />}
                  </div>
                )}
              </div>
            </div>

            <Text type="body2-regular" className={p.isSpeaking ? 'text-green' : isYou ? 'text-white' : 'text-gray-90'}>
              {isYou ? t`Participant ${participantNumber} (You)` : t`Participant ${participantNumber}`}
            </Text>

            {showReadyStatus && badgeStatus && (
              <div className="flex items-center gap-2">
                <span
                  className={clsx(
                    'size-2.5 rounded-full',
                    participantIsReady ? 'bg-green' : 'bg-gray-90 animate-pulse'
                  )}
                />
                <Text type="body2-regular" className="text-gray-90">
                  {participantIsReady ? <Trans>Ready</Trans> : <Trans>Waiting...</Trans>}
                </Text>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
